import {
    Autocomplete,
    Box,
    Chip,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import type { MutableRefObject } from "react";
import Iconify from "src/components/iconify";

type Period = 'l7d' | 'l14d' | 'l30d' | 'l90d';
type Comparison = 'none' | 'pp' | 'ly';
type EvolutionFormat = 'percent' | 'pp';

type Option<T> = {
    value: T;
    label: string;
};

type Props = {
    apiRef: any;
    toolbarRef: MutableRefObject<any>;
    selectedPeriod: Period;
    onPeriodChange: (period: Period) => void;
    selectedComparison: Comparison;
    onComparisonChange: (comparison: Comparison) => void;
    onEvolutionChange: (show: boolean) => void;
    evolutionFormat: EvolutionFormat;
    onEvolutionFormatChange: (format: EvolutionFormat) => void;
    onOpenAnalysisChart: () => void;
};

const periodOptions: Option<Period>[] = [
    { value: 'l7d', label: 'Last week' },
    { value: 'l14d', label: 'Last 14 days' },
    { value: 'l30d', label: 'Last 30 days' },
    { value: 'l90d', label: 'Last 90 days' },
];

const comparisonOptions: Option<Comparison>[] = [
    { value: 'none', label: 'No comparison' },
    { value: 'pp', label: 'Previous period' },
    { value: 'ly', label: 'Last year' },
];

const chipSx = {
    height: 24,
    borderRadius: '7px',
    backgroundColor: 'rgba(145, 158, 171, 0.20)',
    color: '#333C47',
    fontFamily: '"Public Sans"',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '23px',
    transition: 'background-color 0.15s ease',
    '&:hover': {
        backgroundColor: '#DFE3E8',
    },
    '& .MuiChip-label': {
        px: '8px',
        fontFamily: '"Public Sans"',
        fontSize: '13px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '23px',
        color: '#333C47',
    },
};

const autocompleteSx = {
    '& .MuiOutlinedInput-root': {
        height: 32,
        minHeight: 32,
        borderRadius: '8px',
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        paddingLeft: '4px !important',
        paddingRight: '30px !important',
        backgroundColor: '#FFFFFF',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(145, 158, 171, 0.24)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(145, 158, 171, 0.40)',
    },
    '& .MuiAutocomplete-input': {
        width: '1px !important',
        minWidth: '1px !important',
        maxWidth: '1px !important',
        padding: '0 !important',
        margin: '0 !important',
        opacity: 0,
        color: 'transparent',
        caretColor: 'transparent',
        flex: '0 0 1px !important',
    },
    '& .MuiAutocomplete-popupIndicator': {
        p: '2px',
        color: '#637381',
    },
};

export default function ConquestModeToolbar({
    apiRef,
    toolbarRef,
    selectedPeriod,
    onPeriodChange,
    selectedComparison,
    onComparisonChange,
    onEvolutionChange,
    evolutionFormat,
    onEvolutionFormatChange,
    onOpenAnalysisChart,
}: Props) {
    const selectedPeriodOption =
        periodOptions.find((option) => option.value === selectedPeriod) ?? periodOptions[0];

    const selectedComparisonOption =
        comparisonOptions.find((option) => option.value === selectedComparison) ?? comparisonOptions[0];

    const comparisonEnabled = selectedComparison !== 'none';

    return (
        <Stack
            ref={toolbarRef}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={0}
            px={2.5}
            sx={{
                height: '40px',
                minHeight: '40px',
                boxSizing: 'border-box',
                backgroundColor: 'white',
                borderTopRightRadius: '8px',
                borderTopLeftRadius: '8px',
                borderBottom: '1px solid rgba(145, 158, 171, 0.20)',
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
            >
                <Tooltip title="Show/hide columns" placement="top">

                    <IconButton
                        onClick={() =>
                            apiRef.current?.showPreferences('columns')
                        }
                        sx={{
                            width: 26,
                            height: 26,
                            p: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <Iconify
                            icon="mingcute:calendar-week-line"
                            width={18}
                            height={18}
                        />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Filters" placement="top">

                    <IconButton
                        onClick={() =>
                            apiRef.current?.showFilterPanel()
                        }
                        sx={{
                            width: 26,
                            height: 26,
                            p: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <Iconify
                            icon="ic:round-filter-list"
                            width={18}
                            height={18}
                        />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Export" placement="top">
                    <IconButton
                        onClick={() =>
                            apiRef.current?.exportDataAsCsv({
                                fileName: 'conquest-mode',
                                utf8WithBom: true,
                            })
                        }
                        sx={{
                            width: 24,
                            height: 24,
                            p: 0,
                            color: '#919EAB',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                        >
                            <path
                                d="M14 10V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V10M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2"
                                stroke="#919EAB"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </IconButton>
                </Tooltip>
            </Stack>

            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
            >

                <Tooltip
                    title="Show analysis chart"
                    placement="top"
                >
                    <IconButton
                        size="small"
                        onClick={onOpenAnalysisChart}
                        sx={{
                            display: 'flex',
                            padding: '4px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '8px',
                            width: 26,
                            height: 26,
                            minWidth: 26,
                            borderRadius: '6px',
                            backgroundColor: 'rgba(145, 158, 171, 0.20)',
                            '&:hover': {
                                backgroundColor: 'rgba(145, 158, 171, 0.40)',
                            },
                        }}
                    >
                        <Box
                            component="svg"
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            sx={{
                                display: 'block',
                            }}
                        >
                            <path
                                d="M15 15V9.75M9 15V7.5M3 15L3 12M10.055 3.77063L13.9313 5.22425M8.09908 4.05069L3.90017 7.19987M15.7955 4.8295C16.2348 5.26884 16.2348 5.98116 15.7955 6.4205C15.3562 6.85984 14.6438 6.85984 14.2045 6.4205C13.7652 5.98116 13.7652 5.26884 14.2045 4.8295C14.6438 4.39016 15.3562 4.39016 15.7955 4.8295ZM3.7955 7.0795C4.23483 7.51884 4.23483 8.23116 3.7955 8.6705C3.35616 9.10984 2.64384 9.10984 2.2045 8.6705C1.76517 8.23116 1.76517 7.51884 2.2045 7.0795C2.64384 6.64016 3.35616 6.64016 3.7955 7.0795ZM9.7955 2.5795C10.2348 3.01884 10.2348 3.73116 9.7955 4.1705C9.35616 4.60983 8.64384 4.60983 8.2045 4.1705C7.76517 3.73116 7.76517 3.01884 8.2045 2.5795C8.64384 2.14017 9.35616 2.14017 9.7955 2.5795Z"
                                stroke="#5E6D7B"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </Box>
                    </IconButton>
                </Tooltip>


                {comparisonEnabled && (
                    <Tooltip
                        title={
                            evolutionFormat === 'percent'
                                ? 'Showing % evolution — click for pp'
                                : 'Showing pp evolution — click for %'
                        }
                        placement="top"
                    >
                        <Box
                            onClick={() =>
                                onEvolutionFormatChange(
                                    evolutionFormat === 'percent'
                                        ? 'pp'
                                        : 'percent'
                                )
                            }
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '2px',
                                px: '6px',
                                py: '4px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                backgroundColor: 'rgba(145, 158, 171, 0.08)',
                                color: '#1E88E5',
                            }}
                        >
                            <Iconify
                                icon={
                                    evolutionFormat === 'percent'
                                        ? 'material-symbols:percent-rounded'
                                        : 'material-symbols:tag-rounded'
                                }
                                width={16}
                                sx={{
                                    color: 'inherit',
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    lineHeight: '16.8px',
                                    color: 'inherit',
                                    whiteSpace: 'nowrap',
                                    mx: '2px',
                                }}
                            >
                                evolution
                            </Typography>

                            <Iconify
                                icon="mi:switch"
                                width={16}
                                sx={{
                                    color: '#1E88E5',
                                }}
                            />
                        </Box>
                    </Tooltip>
                )}

                <Autocomplete
                    disableClearable
                    size="small"
                    options={periodOptions}
                    value={selectedPeriodOption}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                    }
                    onChange={(_, newValue) => {
                        onPeriodChange(newValue.value);
                    }}
                    sx={{
                        ...autocompleteSx,
                        width: 142,
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <Chip
                                        label={selectedPeriodOption.label}
                                        size="small"
                                        sx={chipSx}
                                    />
                                ),
                            }}
                        />
                    )}
                />

                <Autocomplete
                    disableClearable
                    size="small"
                    options={comparisonOptions}
                    value={selectedComparisonOption}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                    }
                    onChange={(_, newValue) => {
                        onComparisonChange(newValue.value);
                        onEvolutionChange(
                            newValue.value !== 'none'
                        );
                    }}
                    sx={{
                        ...autocompleteSx,
                        width: 160,
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <Chip
                                        label={selectedComparisonOption.label}
                                        size="small"
                                        sx={chipSx}
                                    />
                                ),
                            }}
                        />
                    )}
                />
            </Stack>
        </Stack>
    );
}